import { MeshKeyRotationSchedule, UpdateScheduledTrafficKeyRotationRequest } from "@somewear/api";
import momentTz from "moment-timezone";
import { z } from "zod";

import { IWorkspace } from "../index";

export const getPreferredTimezoneStorageKey = (workspaceId: string) =>
	`mkrSchedule:${workspaceId}:preferredTimezone`;

const tryGetTimezoneAbbreviation = (ianaTimezone: string) => {
	if (ianaTimezone.toLowerCase() === "utc") {
		return "UTC";
	}

	try {
		const abbreviation = momentTz.tz(ianaTimezone).format("z");
		return abbreviation;
	} catch (error) {
		return ianaTimezone;
	}
};

export enum MeshRotationIntervalUnitIds {
	NONE = MeshKeyRotationSchedule.IntervalUnit.NONE,
	WEEK = MeshKeyRotationSchedule.IntervalUnit.WEEK,
	MONTH = MeshKeyRotationSchedule.IntervalUnit.MONTH,
}

export const rotationScheduleIntervalMultiplierOptions = Array.from(
	{ length: 12 },
	(_, i) => i + 1,
).map((i) => ({
	id: i.toString(),
	value: i,
	label: i.toString(),
}));

export const rotationScheduleIntervalUnitOptions = [
	{
		id: "WEEKS",
		value: MeshRotationIntervalUnitIds.WEEK,
		label: "Weeks",
	},
	{
		id: "MONTHS",
		value: MeshRotationIntervalUnitIds.MONTH,
		label: "Months",
	},
];

export const dayHourOptions = Array.from({ length: 24 }, (_, i) => i).map((i) => {
	const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
	const meridiem = i < 12 ? "AM" : "PM";

	return {
		id: i.toString(),
		value: i,
		label: `${hour12}:00 ${meridiem}`,
	};
});

export const timezoneOptions = (() => {
	const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const allTimezones = ["UTC"];

	if (localTimezone) {
		allTimezones.push(localTimezone);
	}

	return allTimezones.map((timezone) => {
		const label = tryGetTimezoneAbbreviation(timezone);
		const isLocal = timezone.toLowerCase() === localTimezone.toLowerCase();

		return {
			id: timezone,
			value: timezone,
			label: isLocal ? `${label} (Local)` : label,
		};
	});
})();

// Schemas

const meshRotationScheduleSchema = z.object({
	nextRotationTime: z.number(),
	interval: z.number().default(1),
	intervalUnit: z
		.nativeEnum(MeshRotationIntervalUnitIds)
		.default(MeshRotationIntervalUnitIds.WEEK),
});

export const scheduledMeshKeyRotationRequestSchema = z.object({
	workspaceId: z.string(),
	schedule: meshRotationScheduleSchema,
});

const DEFAULT_ROTATION_SCHEDULE = {
	// nextRotationDate will be generated.
	interval: 1,
	intervalUnit: MeshRotationIntervalUnitIds.WEEK,
	timezone: "UTC",
};

export const updateScheduledMeshKeyRotationFormSchema = z.object({
	nextRotationTime: z.date(),
	timezone: z.string().default(DEFAULT_ROTATION_SCHEDULE.timezone),
	interval: z.number().default(DEFAULT_ROTATION_SCHEDULE.interval),
	intervalUnit: z
		.nativeEnum(MeshRotationIntervalUnitIds)
		.default(DEFAULT_ROTATION_SCHEDULE.intervalUnit),
});

export type MeshRotationScheduleSchema = z.infer<typeof meshRotationScheduleSchema>;
export type ScheduledMeshKeyRotationRequestSchema = z.infer<
	typeof scheduledMeshKeyRotationRequestSchema
>;
export type UpdateScheduledMeshKeyRotationFormSchema = z.infer<
	typeof updateScheduledMeshKeyRotationFormSchema
>;

export function convertScheduleMeshKeyFormToRequestSchema(
	workspaceId: string,
	formSchema: UpdateScheduledMeshKeyRotationFormSchema,
): UpdateScheduledTrafficKeyRotationRequest.AsObject {
	const normalizedGmtTime = momentTz(formSchema.nextRotationTime).tz(formSchema.timezone).utc();
	return {
		workspaceId: workspaceId,
		schedule: {
			nextRotationTime: normalizedGmtTime.unix(),
			skippedRotationTime: 0,
			interval: formSchema.interval,
			intervalUnit:
				formSchema.intervalUnit as MeshKeyRotationSchedule.IntervalUnitMap[keyof MeshKeyRotationSchedule.IntervalUnitMap],
		},
	};
}

export function getSoonestPossibleMeshRotationDate(isStaff: boolean, timezone?: string) {
	const currentMoment = momentTz();

	const minimumRotationMoment = currentMoment
		.clone()
		.add(isStaff ? 0 : 7, "days")
		.hour(isStaff ? currentMoment.hour() + 1 : 12)
		.minute(0)
		.second(0)
		.millisecond(0);

	if (timezone) {
		minimumRotationMoment.tz(timezone);
	}

	return minimumRotationMoment.toDate();
}

export function getInitialScheduleValuesFormSchema(
	activeWorkspace: IWorkspace,
	isStaff = false,
): UpdateScheduledMeshKeyRotationFormSchema {
	const existingTime = activeWorkspace.trafficKeyRotationSchedule?.nextRotationTime ?? 0;
	const preferredTimezone = localStorage.getItem(
		getPreferredTimezoneStorageKey(activeWorkspace.id),
	);

	if (existingTime === 0) {
		const nextScheduledRotationTime = getSoonestPossibleMeshRotationDate(
			isStaff,
			preferredTimezone ?? undefined,
		);
		return {
			...DEFAULT_ROTATION_SCHEDULE,
			nextRotationTime: nextScheduledRotationTime,
		};
	} else {
		const existingMoment = momentTz.unix(existingTime).utc();

		if (preferredTimezone) {
			existingMoment.tz(preferredTimezone);
		}

		return {
			nextRotationTime: existingMoment.toDate(),
			interval: activeWorkspace.trafficKeyRotationSchedule?.interval ?? 1,
			intervalUnit:
				activeWorkspace.trafficKeyRotationSchedule?.intervalUnit ??
				MeshRotationIntervalUnitIds.WEEK,
			timezone: preferredTimezone ? preferredTimezone : "UTC",
		};
	}
}
